import {
  ParagonCrmProvider,
  paragonCrmProviderFor,
  useIsParagonCrmEnabled,
} from "components/integrations/managed_integrations/crmProviders";
import { useParagonCrmProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import useParagonUser from "lib/hooks/api/integrations/useParagonUser";

/**
 * The CRM this company pushes to through Paragon, or nothing when it has none it may use.
 *
 * Three things have to agree, and each answers a different question. The install says *which* CRM,
 * since only one is connected at a time and it is what the service resolves a push against. Paragon
 * says whether that connection is still *live* — it holds the credential, and ours is only a record
 * that one was made. The flag says whether this user may reach it at all: the install belongs to
 * the company but the flag is evaluated per user, so a colleague of whoever connected can have it
 * off, and showing them a send button would only earn them a 403 on every push. Each CRM has its
 * own flag, so one CRM's rollout never unlocks another's.
 */
export function useConnectedParagonCrm(
  /**
   * Off for a call site that is not offering a Paragon CRM. Resolving one authenticates the
   * Paragon SDK, which is a token request and a round trip that such a caller gets nothing for.
   */
  enabled = true,
): ParagonCrmProvider | undefined {
  const { data: install } = useParagonCrmProvider({ enabled });
  const isParagonCrmEnabled = useIsParagonCrmEnabled();

  // Settled before Paragon is asked anything. The install and the flag are both answers we already
  // have, and neither costs a round trip — so a company on a native CRM, or on none, or a user the
  // flag is off for, never authenticates an SDK that could tell them nothing they can act on.
  const provider = paragonCrmProviderFor(install);
  const eligible = !!provider && isParagonCrmEnabled(provider);

  const { user } = useParagonUser(enabled && eligible);

  if (!provider || !eligible) return undefined;

  return user?.integrations?.[provider.integrationKey]?.enabled ? provider : undefined;
}
